import { UX_JOURNEY_EVENTS, type AbandonmentReason } from '~/types/ux-journey';

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return;

  const {
    initJourney,
    handleNavigation,
    getJourneyContext,
    computeMetrics,
    getState,
    shouldSignalCartAbandonment,
    getFunnelStepFromPath,
  } = useUserJourney();
  const { trackJourney } = useJourneyTracker();

  const state = initJourney();
  trackJourney(UX_JOURNEY_EVENTS.SESSION_START, {
    session_started_at: state.session_started_at,
    initial_path: state.last_route.path,
  });

  const router = useRouter();

  router.afterEach((to, _) => {
    const { dwell_time_ms, from_path, funnel_step, entered_funnel_step } =
      handleNavigation(to.path, to.name?.toString());

    const journeyState = getState();
    trackJourney(UX_JOURNEY_EVENTS.NAVIGATION, {
      from_path,
      to_path: to.path,
      dwell_time_ms,
      funnel_step,
      metrics: computeMetrics(journeyState),
      page_sequence: journeyState.page_sequence,
      cart_visit_count: journeyState.cart_visit_count,
      cart_snapshot: journeyState.cart_snapshot,
    });

    if (entered_funnel_step) {
      trackJourney(UX_JOURNEY_EVENTS.FUNNEL_ENTER, {
        funnel_step: entered_funnel_step,
        to_path: to.path,
        metrics: computeMetrics(journeyState),
      });
    }
  });

  const emitCartAbandonment = (reason: AbandonmentReason) => {
    if (!shouldSignalCartAbandonment()) return;

    const journeyState = getState();
    trackJourney(
      UX_JOURNEY_EVENTS.CART_ABANDONMENT,
      {
        reason,
        funnel_step: getFunnelStepFromPath(router.currentRoute.value.path),
        cart_snapshot: journeyState.cart_snapshot,
        metrics: computeMetrics(journeyState),
        page_sequence: journeyState.page_sequence,
      },
      { immediate: true },
    );
  };

  const onVisibilityChange = () => {
    const context = getJourneyContext();
    const journeyState = getState();

    if (document.visibilityState === 'hidden') {
      trackJourney(
        UX_JOURNEY_EVENTS.SESSION_PAUSE,
        {
          ...context,
          metrics: computeMetrics(journeyState),
          cart_snapshot: journeyState.cart_snapshot,
        },
        { immediate: true },
      );
      emitCartAbandonment('tab_hidden');
      return;
    }

    trackJourney(UX_JOURNEY_EVENTS.SESSION_RESUME, {
      ...context,
      metrics: computeMetrics(journeyState),
    });
  };

  const onPageHide = () => {
    const context = getJourneyContext();
    const journeyState = getState();

    emitCartAbandonment('session_exit');

    trackJourney(
      UX_JOURNEY_EVENTS.SESSION_EXIT,
      {
        ...context,
        metrics: computeMetrics(journeyState),
        cart_snapshot: journeyState.cart_snapshot,
        page_sequence: journeyState.page_sequence,
      },
      { immediate: true, useBeacon: true },
    );
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
});
